import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Activity, AlertCircle, Image, Check, CheckCheck, BellRing, Settings, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "../lib/api";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import useWebSocketNotifications from "@/hooks/useWebSocketNotifications";
import usePushNotifications from "@/hooks/usePushNotifications";


const NOTIFICATION_ICONS = {
  activity: Activity,
  compliance_report: AlertCircle,
  work_sample: Image,
  milestone: Star
};

const NOTIFICATION_COLORS = {
  activity: "bg-blue-100 text-blue-600",
  compliance_report: "bg-red-100 text-red-600",
  work_sample: "bg-purple-100 text-purple-600",
  milestone: "bg-amber-100 text-amber-600"
};

export default function NotificationBell({ location = '' }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  // Push notification hook
  const {
    permission: pushPermission,
    subscription: pushSubscription,
    isSupported: isPushSupported,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush
  } = usePushNotifications();

  // Handle new real-time notification
  const handleNewNotification = useCallback((notification) => {
    // Add to top of list
    setNotifications(prev => [notification, ...prev.filter(n => n.id !== notification.id)]);
    setUnreadCount(prev => prev + 1);
    
    // Show toast notification
    toast(notification.title, {
      description: notification.message,
      action: notification.child_id ? {
        label: "View",
        onClick: () => navigate(`/children/${notification.child_id}`)
      } : undefined
    });
  }, [navigate]);

  // WebSocket connection for real-time updates
  const { isConnected: wsConnected } = useWebSocketNotifications(handleNewNotification);

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get(`/parent/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      
      const response = await api.get(`/parent/notifications`, {
        
        params: { limit: 20 }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      
      await api.post(
        `/parent/notifications/${notification.id}/mark-read`,
        {},
        
      );
      
      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    // Navigate based on type
    if (notification.notification_type === "compliance_report") {
      navigate(`/reports/${notification.reference_id}`);
    } else if (notification.notification_type === "activity") {
      navigate(`/children/${notification.child_id}`);
    } else if (notification.notification_type === "work_sample") {
      navigate(`/children/${notification.child_id}`);
    } else if (notification.notification_type === "milestone") {
      navigate(`/children/${notification.child_id}`);
    } else if (notification.child_id) {
      // Fallback: navigate to child profile for any notification with a child_id
      navigate(`/children/${notification.child_id}`);
    }
    
    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    try {
      
      await api.post(
        `/parent/notifications/mark-all-read`,
        {},
        
      );
      
      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  return (
    <div ref={dropdownRef} className="relative" data-testid={`notification-bell${location === 'desktop' ? '-desktop' : location === 'mobile' ? '-mobile' : ''}`}>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleOpen}
        className="relative"
        data-testid={`notification-bell-button${location === 'desktop' ? '-desktop' : location === 'mobile' ? '-mobile' : ''}`}
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Notifications</h3>
            <div className="flex items-center gap-2">
              {!showSettings && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-sage-600 hover:text-sage-700 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1 hover:bg-slate-100 rounded"
                title="Notification settings"
              >
                <Settings className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-500">Loading...</div>
            ) : showSettings ? (
              <div className="p-4 space-y-4">
                <button 
                  onClick={() => setShowSettings(false)} 
                  className="text-sm text-sage-600 hover:text-sage-700"
                >
                  ← Back to notifications
                </button>
                
                <div className="space-y-3">
                  {/* Real-time connection status */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Real-time Updates</p>
                      <p className="text-xs text-slate-500">WebSocket connection</p>
                    </div>
                    <span className={`flex items-center gap-1 text-xs ${wsConnected ? "text-green-600" : "text-slate-400"}`}>
                      <span className={`w-2 h-2 rounded-full ${wsConnected ? "bg-green-500" : "bg-slate-300"}`}></span>
                      {wsConnected ? "Connected" : "Disconnected"}
                    </span>
                  </div>
                  
                  {/* Push notifications */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Push Notifications</p>
                        <p className="text-xs text-slate-500">
                          {!isPushSupported 
                            ? "Not supported in this browser" 
                            : pushPermission === "denied"
                              ? "Blocked in browser settings"
                              : pushSubscription 
                                ? "Enabled" 
                                : "Receive alerts when browser is closed"}
                        </p>
                      </div>
                      {isPushSupported && pushPermission !== "denied" && (
                        pushSubscription ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await unsubscribePush();
                                toast.success("Push notifications disabled");
                              } catch {
                                toast.error("Failed to disable push notifications");
                              }
                            }}
                            disabled={pushLoading}
                            className="text-xs"
                          >
                            {pushLoading ? "..." : "Disable"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await subscribePush();
                                toast.success("Push notifications enabled!");
                              } catch (e) {
                                toast.error(e.message || "Failed to enable push notifications");
                              }
                            }}
                            disabled={pushLoading}
                            className="bg-sage-500 hover:bg-sage-600 text-white text-xs"
                          >
                            {pushLoading ? "..." : "Enable"}
                          </Button>
                        )
                      )}
                    </div>
                    {pushPermission === "denied" && (
                      <p className="text-xs text-amber-600 mt-2">
                        Please enable notifications in your browser settings to receive push alerts.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : notifications.length > 0 ? (
              <div>
                {notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.notification_type] || Bell;
                  const colorClass = NOTIFICATION_COLORS[notification.notification_type] || "bg-slate-100 text-slate-600";
                  const isUnread = !notification.read_at;
                  
                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 ${
                        isUnread ? "bg-sage-50/50" : ""
                      }`}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${isUnread ? "font-semibold text-slate-800" : "text-slate-700"}`}>
                            {notification.title}
                          </p>
                          {isUnread && (
                            <span className="w-2 h-2 bg-sage-500 rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">
                            {notification.child_name}
                          </span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-400">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No notifications yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  You'll be notified of activities and reports
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
